import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { BdsIcon, BdsChipClickable } from '@/core/constants/webComponents';
import { useDebouncedSearch } from '@/core/hooks/useDebouncedSearch';
import { useClickOutside } from '@/core/hooks/useClickOutside';
import { useDropdownAutoScroll } from '@/core/hooks/useDropdownAutoScroll';
import locationsService from '@/core/services/LocationService';
import type { LocationItem } from '@/core/services/interfaces/LocationInterfaces';
import './LocationSearchSelect.scss';

const MINIMUM_SEARCH_LENGTH = 3;

export type LocationOption = LocationItem;

export interface LocationSearchSelectProps {
  label: string;
  placeholder: string;
  helperText: string;
  loadingText: string;
  emptyText: string;
  selectedLocations: LocationOption[];
  onSelectionChange: (locations: LocationOption[]) => void;
}

const getLocationKey = (location: LocationOption): string => {
  const citySuffix = location.city ? `__${location.city}` : '';
  const stateSuffix = location.state ? `__${location.state}` : '';
  return `${location.country}${stateSuffix}${citySuffix}`;
};

const formatLabel = (location: LocationOption): string => {
  return [location.city, location.state, location.country].filter(Boolean).join(', ');
};

export const LocationSearchSelect: React.FC<LocationSearchSelectProps> = ({
  label,
  placeholder,
  helperText,
  loadingText,
  emptyText,
  selectedLocations,
  onSelectionChange,
}) => {
  const searchFn = useCallback(
    (term: string) => locationsService.getLocations({ location: term }).then((r) => r.locations),
    []
  );

  const {
    searchTerm,
    isOpen,
    isLoading,
    searchResults,
    handleSearchTermChange,
    closeDropdown,
  } = useDebouncedSearch<LocationOption>({ searchFn, minLength: MINIMUM_SEARCH_LENGTH });

  const containerRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLLabelElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const chipRefs = useRef<(HTMLElement | null)[]>([]);

  const outsideRefs = useMemo(() => [fieldRef, dropdownRef], []);

  useClickOutside(outsideRefs, closeDropdown, isOpen);

  const isLocationSelected = useCallback(
    (option: LocationOption): boolean =>
      selectedLocations.some((selected) => getLocationKey(selected) === getLocationKey(option)),
    [selectedLocations]
  );

  const visibleResults = searchResults.filter((option) => !isLocationSelected(option));

  const handleSelectLocation = (option: LocationOption) => {
    onSelectionChange([...selectedLocations, option]);
    closeDropdown();
  };

  const handleRemoveLocation = useCallback(
    (option: LocationOption) => {
      onSelectionChange(
        selectedLocations.filter((selected) => getLocationKey(selected) !== getLocationKey(option))
      );
    },
    [selectedLocations, onSelectionChange]
  );

  useEffect(() => {
    const cleanups: (() => void)[] = [];
    selectedLocations.forEach((location, index) => {
      const chipElement = chipRefs.current[index];
      if (!chipElement) return;

      const closeHandler = (event: Event) => {
        event.stopPropagation();
        handleRemoveLocation(location);
      };

      chipElement.addEventListener('chipClickableClose', closeHandler);
      cleanups.push(() => chipElement.removeEventListener('chipClickableClose', closeHandler));
    });
    return () => cleanups.forEach((cleanup) => cleanup());
  }, [selectedLocations, handleRemoveLocation]);

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const firstResult = visibleResults[0];
    if (event.key === 'Enter' && firstResult) {
      event.preventDefault();
      handleSelectLocation(firstResult);
    }
    if (event.key === 'Escape') closeDropdown();
  };

  const isSearchActive = searchTerm.trim().length >= MINIMUM_SEARCH_LENGTH;
  const showDropdown = isOpen && isSearchActive;
  const hasVisibleResults = visibleResults.length > 0;

  useDropdownAutoScroll(showDropdown, dropdownRef, selectedLocations.length);

  return (
    <div className="location-search" ref={containerRef}>
      <span id="location-input-label" className="location-search__label">
        {label}
      </span>

      <div className="location-search__wrapper">
        <label
          ref={fieldRef}
          className={`location-search__field${showDropdown ? ' location-search__field--open' : ''}`}
          htmlFor="location-input-field"
        >
          {selectedLocations.map((location, index) => (
            <button
              key={getLocationKey(location)}
              type="button"
              className="location-search__chip-wrapper"
              onClick={(event) => event.stopPropagation()}
            >
              <BdsChipClickable
                ref={(element: HTMLElement | null) => { chipRefs.current[index] = element; }}
                color="outline"
                close
              >
                <span className="location-search__chip-content">
                  {formatLabel(location)}
                </span>
              </BdsChipClickable>
            </button>
          ))}

          <input
            id="location-input-field"
            ref={searchInputRef}
            className="location-search__input"
            value={searchTerm}
            placeholder={selectedLocations.length === 0 ? placeholder : ''}
            onChange={handleSearchTermChange}
            onKeyDown={handleInputKeyDown}
            autoComplete="off"
            aria-labelledby="location-input-label"
          />

          <span className={`location-search__arrow${showDropdown ? ' location-search__arrow--open' : ''}`} aria-hidden="true">
            <BdsIcon name="arrow-down" size="x-small" />
          </span>
        </label>

        {showDropdown && (
          <div className="location-search__dropdown" ref={dropdownRef}>
            <ul className="location-search__list">
              {isLoading && (
                <li className="location-search__option location-search__option--empty">
                  <span className="location-search__option-text">{loadingText}</span>
                </li>
              )}

              {!isLoading && !hasVisibleResults && (
                <li className="location-search__option location-search__option--empty">
                  <span className="location-search__option-text">{emptyText}</span>
                </li>
              )}

              {!isLoading && hasVisibleResults && visibleResults.map((option) => (
                <li className="location-search__option" key={getLocationKey(option)}>
                  <button
                    type="button"
                    className="location-search__option-button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => handleSelectLocation(option)}
                  >
                    {formatLabel(option)}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <span className="location-search__helper">
        <BdsIcon name="info" size="x-small" />
        {helperText}
      </span>
    </div>
  );
};